/// <amd-module name="Wit/Common/WorkItemControls/NodeUtils" />

import { IListBoxItem } from "VSSUI/ListBox";
import { FutureDate } from "Wit/Common/WorkItemControls/Constants";
import { TreeNodeStructureType, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";

/**
 * Recursively convert a WorkItemClassificationNode tree to list box items with parent pointers
 */
export function convertNodesToListBoxItems(
    node: WorkItemClassificationNode,
    parent?: IListBoxItem<WorkItemClassificationNode>
): IListBoxItem<WorkItemClassificationNode>[] {
    const item: IListBoxItem<WorkItemClassificationNode> = { id: node.identifier, data: node, parent, text: normalizePath(node.path) };
    const items = [item];
    if (node.hasChildren) {
        const compareFn = node.structureType === TreeNodeStructureType.Area ? compareAreaPaths : compareIterations;
        const children = [...node.children].sort(compareFn);
        for (const child of children) {
            items.push(...convertNodesToListBoxItems(child, item));
        }
    }

    return items;
}

const compareAreaPaths = (a: WorkItemClassificationNode, b: WorkItemClassificationNode): number => a.name.localeCompare(b.name, navigator.language);

/**
 * Iteration comparison steps:
 * Try to compare start dates
 * If only one item has a start date, that item is sorted ahead of the other item
 * If neither item has a start date just compare names
 */
export const compareIterations = (a: WorkItemClassificationNode, b: WorkItemClassificationNode): number => {
    const aStartDate = (a.attributes && (a.attributes["startDate"] as Date)) || FutureDate;
    const bStartDate = (b.attributes && (b.attributes["startDate"] as Date)) || FutureDate;

    if (aStartDate !== bStartDate) {
        return aStartDate.getTime() - bStartDate.getTime();
    }

    return a.name.localeCompare(b.name, navigator.language);
};

/**
 * Normalize an iteration or area path
 */
export function normalizePath(path: string | undefined, pathSeparator = "\\"): string {
    if (!path) {
        return "";
    }

    if (path.startsWith(pathSeparator)) {
        path = path.substring(1);
    }

    // Iteration paths: Format "\MyAgileProj\Iteration\Iteration 1" => "MyAgileProj\Iteration 1"
    // Area paths: Format "\MyAgileProj\Area\my area" => "MyAgileProj\my area"
    const pathParts = path.split("\\");
    // remove the "Iteration" or "Area" part of the path
    pathParts.splice(1, 1);
    return pathParts.join(pathSeparator);
}

/**
 * Check if a value is undefined or an empty object
 */
export function isEmpty(val: unknown): boolean {
    return !val || (Object.getPrototypeOf(val) === Object.prototype && Object.keys(val as Object).length === 0);
}
